import type { PatternDesignOptimisation } from '~api/services';
import type { ColumnFilterConfig, RangeFilterOption } from '~components/TableList.types';
import { calculateNiceBoundaries, calculateValueRange } from '~utils';

type NumericColumnKey = 'efficiency' | 'bench_height' | 'burden' | 'spacing' | 'diameter' | 'holes';

type ColumnMetadata = {
  columnId: NumericColumnKey;
  displayLabel: string;
  getValue: (row: PatternDesignOptimisation) => number;
  formatLabel: (value: number) => string;
};

/** Column metadata for generating filter options */
const COLUMN_METADATA: ColumnMetadata[] = [
  {
    columnId: 'efficiency',
    displayLabel: 'Efficiency',
    getValue: (row) => row.efficiency,
    formatLabel: (v) => `${(v * 100).toFixed(0)}%`,
  },
  {
    columnId: 'bench_height',
    displayLabel: 'Bench Height',
    getValue: (row) => row.bench_height,
    formatLabel: (v) => `${v} m`,
  },
  {
    columnId: 'burden',
    displayLabel: 'Burden',
    getValue: (row) => row.burden,
    formatLabel: (v) => `${v} m`,
  },
  {
    columnId: 'spacing',
    displayLabel: 'Spacing',
    getValue: (row) => row.spacing,
    formatLabel: (v) => `${v} m`,
  },
  {
    columnId: 'diameter',
    displayLabel: 'Diameter',
    getValue: (row) => row.diameter,
    formatLabel: (v) => `${(v * 1000).toFixed(0)} mm`,
  },
  {
    columnId: 'holes',
    displayLabel: 'Holes',
    getValue: (row) => row.holes,
    formatLabel: (v) => `${v}`,
  },
];

/**
 * Generates "greater than" style filter options: "> X"
 */
const generateGreaterThanOptions = (
  boundaries: number[],
  formatLabel: (value: number) => string,
): RangeFilterOption[] => {
  if (boundaries.length === 0) return [];

  return boundaries.map((boundary) => ({
    label: `> ${formatLabel(boundary)}`,
    min: boundary,
  }));
};

/**
 * Generates filter configurations dynamically based on the provided data.
 * Analyzes data distribution to create meaningful "greater than" filter options.
 *
 * @param data - Array of optimisation data to analyze
 * @param buckets - Number of filter buckets to generate (default: 4)
 * @returns Array of filter configurations with data-driven options
 */
const generateFilterConfigs = (
  data: PatternDesignOptimisation[],
  buckets = 4,
): ColumnFilterConfig<PatternDesignOptimisation>[] => {
  if (data.length === 0) return [];

  const filters = COLUMN_METADATA.map((meta) => {
    const { min, max } = calculateValueRange(data, meta.getValue);

    // Skip filter if all values are the same
    if (min === max || Number.isNaN(min) || Number.isNaN(max)) return null;

    const boundaries = calculateNiceBoundaries(min, max, buckets);
    const options = generateGreaterThanOptions(boundaries, meta.formatLabel);

    if (options.length === 0) return null;

    return {
      columnId: meta.columnId,
      displayLabel: meta.displayLabel,
      type: 'range' as const,
      getValue: meta.getValue,
      options,
    };
  }).filter((config): config is NonNullable<typeof config> => config !== null);

  return filters;
};

export { COLUMN_METADATA, generateFilterConfigs, generateGreaterThanOptions };
